import { useMemo, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq, ilike } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { setQuery, useRoute } from "../lib/router";
import { useSession } from "../lib/session";
import { useRefData } from "../lib/refdata";
import { PageHead } from "./Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Seg, Select, Sheet, fmtShort, useToast } from "./ui";
import { openPerson } from "./PersonDrawer";
import { RemindMeButton } from "./Bell";

export type Task = {
  id: string; title: string; notes: string | null; status: string;
  priority: "Urgent" | "High" | "Normal" | "Low"; due_on: string | null; owner_user_id: string | null; owner_role: string | null;
  person_id: string | null; opportunity_id: string | null; checklist: { text: string; done: boolean }[];
  repeat: "daily" | "weekdays" | "weekly" | "monthly" | null; source: string; blocked_reason: string | null;
  created_by: string | null; completed_at: string | null; created_at: string;
  person?: { full_name: string } | null;
};
export const STATUSES: string[] = ["To do", "Doing", "Blocked", "Done"];
/** Task stages in your order (Settings: Tasks → Edit stages). "To do" first, "Done" last. */
export function useTaskStages(): string[] {
  const ref = useRefData();
  const v = ref.data?.setting("task_statuses");
  const mid = (Array.isArray(v) ? v : STATUSES).filter((x: string) => x !== "To do" && x !== "Done");
  return ["To do", ...mid, "Done"];
}
const PRIORITIES: Task["priority"][] = ["Urgent", "High", "Normal", "Low"];
export const ROLE_QUEUE: Record<string, string> = { assistant: "Assistant (whoever holds the role)", leader: "Team leaders", admin: "Admins" };
const REPEAT: Record<string, string> = { daily: "Every day", weekdays: "Every weekday", weekly: "Every week", monthly: "Every month" };
const SELECT = "*,person:people(full_name)";

type Assignee = { user_id: string; display_name: string; role_key: string };
export const useAssignees = () => useQuery("tasks:assignees", () => db.rpc<Assignee[]>("task_assignees"), { staleMs: 300_000 });

/** How late a task is, in days (0 = due today, negative = not yet due, null = no date). */
export const lateBy = (t: { due_on: string | null; status: string }) =>
  !t.due_on || t.status === "Done" ? null : dayjs().startOf("day").diff(dayjs(t.due_on), "day");

export function ownerLabel(t: Pick<Task, "owner_user_id" | "owner_role">, people: Assignee[] | undefined, me?: string) {
  if (t.owner_user_id) return t.owner_user_id === me ? "Me" : people?.find((p) => p.user_id === t.owner_user_id)?.display_name ?? "Someone";
  if (t.owner_role) return ROLE_QUEUE[t.owner_role]?.split(" (")[0] ?? t.owner_role;
  return "Unassigned";
}

export function DueChip({ t }: { t: Task }) {
  const late = lateBy(t);
  if (t.status === "Done") return <span className="pill warm">Done</span>;
  if (late == null) return <span className="muted small">No date</span>;
  if (late > 0) return <span className={clsx("pill", late >= 3 ? "alert" : "cooling")}>{late === 1 ? "1 day late" : `${late} days late`}</span>;
  if (late === 0) return <span className="pill cooling">Today</span>;
  return <span className="small muted">{late === -1 ? "Tomorrow" : fmtShort(t.due_on)}</span>;
}

const prioCls = (p: Task["priority"]) => (p === "Urgent" ? "alert" : p === "High" ? "cooling" : "cold");

export function TaskRow({ t, onOpen, people, me, compact }: { t: Task; onOpen: (t: Task) => void; people?: Assignee[]; me?: string; compact?: boolean }) {
  const toast = useToast();
  const done = t.checklist.filter((c) => c.done).length;
  async function tick(e: React.MouseEvent) {
    e.stopPropagation();
    await db.update("tasks", { id: eq(t.id) }, { status: t.status === "Done" ? "To do" : "Done" });
    invalidate("tasks"); invalidate("cc");
    if (t.status !== "Done") toast(t.repeat ? `Done · next one set (${REPEAT[t.repeat].toLowerCase()})` : "Done");
  }
  return (
    <div className={clsx("task-row", t.status === "Done" && "is-done")} role="button" tabIndex={0} onClick={() => onOpen(t)} onKeyDown={(e) => e.key === "Enter" && onOpen(t)}>
      <button className="task-check" aria-label={t.status === "Done" ? "Mark not done" : "Mark done"} aria-pressed={t.status === "Done"} onClick={tick}>{t.status === "Done" ? "✓" : ""}</button>
      <div className="task-main">
        <div className="task-title">{t.title}</div>
        <div className="task-meta small">
          {t.person?.full_name && <span>{t.person.full_name}</span>}
          {!compact && <span>{ownerLabel(t, people, me)}</span>}
          {t.checklist.length > 0 && <span>☑ {done}/{t.checklist.length}</span>}
          {t.repeat && <span>↻ {REPEAT[t.repeat]}</span>}
          {t.status === "Blocked" && <span className="pill alert">Blocked{t.blocked_reason ? `: ${t.blocked_reason}` : ""}</span>}
          {t.status !== "To do" && t.status !== "Done" && t.status !== "Blocked" && <span className="pill cold">{t.status}</span>}
        </div>
      </div>
      <div className="task-side">
        {t.priority !== "Normal" && <span className={clsx("pill", prioCls(t.priority))}>{t.priority}</span>}
        <DueChip t={t} />
      </div>
    </div>
  );
}

/** Create or edit a task. `preset` pre-fills a new one (e.g. from a person). */
export function TaskSheet({ task, preset, onClose }: { task: Task | null; preset?: Partial<Task> & { person_name?: string }; onClose: () => void }) {
  const toast = useToast();
  const { session, can } = useSession();
  const me = session?.user.id;
  const people = useAssignees();
  const stages = useTaskStages();
  const [f, setF] = useState<Partial<Task>>(task ?? { status: "To do", priority: "Normal", owner_user_id: me, checklist: [], ...preset });
  const [person, setPerson] = useState<{ id: string; name: string } | null>(
    task?.person_id ? { id: task.person_id, name: task.person?.full_name ?? "" } : preset?.person_id ? { id: preset.person_id, name: preset.person_name ?? "" } : null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(dq.length > 1 ? `task-find:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", full_name: ilike(dq), deleted_at: "is.null", limit: 8 }));
  const [item, setItem] = useState("");
  const [err, setErr] = useState<Error | null>(null);
  const comments = useQuery(task ? `tasks:comments:${task.id}` : null, () =>
    db.select<{ id: string; body: string; author: string; created_at: string }>("task_comments", { task_id: eq(task!.id), select: "*", order: "created_at" }));
  const [comment, setComment] = useState("");
  const editable = can("tasks", "edit");
  const set = <K extends keyof Task>(k: K, v: Task[K] | null) => setF((x) => ({ ...x, [k]: v }));

  const ownerValue = f.owner_user_id ? `u:${f.owner_user_id}` : f.owner_role ? `r:${f.owner_role}` : "";
  const ownerOptions = [
    ...(people.data ?? []).map((p) => ({ value: `u:${p.user_id}`, label: p.user_id === me ? `Me (${p.display_name})` : p.display_name })),
    ...Object.entries(ROLE_QUEUE).map(([k, l]) => ({ value: `r:${k}`, label: `Queue · ${l}` })),
  ];

  async function save() {
    setErr(null);
    try {
      if (!f.title?.trim()) throw new Error("Give the task a title.");
      if (f.status === "Blocked" && !f.blocked_reason?.trim()) throw new Error("Say what it's blocked on, so someone can unblock it.");
      const body = {
        title: f.title.trim(), notes: f.notes || null, status: f.status, priority: f.priority, due_on: f.due_on || null,
        owner_user_id: f.owner_user_id ?? null, owner_role: f.owner_user_id ? null : f.owner_role ?? null,
        person_id: person?.id ?? null, opportunity_id: f.opportunity_id ?? null, checklist: f.checklist ?? [],
        repeat: f.repeat || null, blocked_reason: f.status === "Blocked" ? f.blocked_reason : null,
      };
      if (task) await db.update("tasks", { id: eq(task.id) }, body);
      else await db.insert("tasks", body);
      invalidate("tasks"); invalidate("cc");
      toast(task ? "Task saved" : body.owner_user_id && body.owner_user_id !== me ? "Task assigned · they'll get an email" : "Task added");
      onClose();
    } catch (e) { setErr(e as Error); }
  }
  async function remove() {
    if (!task) return;
    try { await db.remove("tasks", { id: eq(task.id) }); invalidate("tasks"); invalidate("cc"); toast("Task deleted"); onClose(); }
    catch (e) { setErr(e as Error); }
  }
  async function addComment() {
    if (!task || !comment.trim()) return;
    try { await db.insert("task_comments", { task_id: task.id, body: comment.trim() }); setComment(""); invalidate(`tasks:comments:${task.id}`); }
    catch (e) { setErr(e as Error); }
  }
  const cl = f.checklist ?? [];

  return (
    <Sheet title={task ? "Task" : "New task"} onClose={onClose} footer={<>
      {task && (task.created_by === me || can("users", "view")) && <button className="btn ghost" onClick={remove}>Delete</button>}
      {person && <button className="btn ghost" onClick={() => openPerson(person.id)}>Open person</button>}
      {task && <RemindMeButton task={{ id: task.id, title: task.title }} person={person ? { id: person.id, full_name: (person as any).full_name ?? (person as any).name ?? "" } : undefined} />}
      {editable && <button className="btn primary" onClick={save}>{task ? "Save" : "Add task"}</button>}
    </>}>
      <ErrorBox error={err} />
      <Field label="What needs doing" wide><input className="input" autoFocus={!task} value={f.title ?? ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Call Ada about her RESP" /></Field>
      <div className="form-grid">
        <Field label="Owner"><Select value={ownerValue} options={ownerOptions} placeholder="Unassigned"
          onChange={(v) => setF((x) => ({ ...x, owner_user_id: v.startsWith("u:") ? v.slice(2) : null, owner_role: v.startsWith("r:") ? v.slice(2) : null }))} /></Field>
        <Field label="Due"><input type="date" className="input" value={f.due_on ?? ""} onChange={(e) => set("due_on", e.target.value || null)} /></Field>
        <Field label="Stage"><Select value={f.status ?? "To do"} onChange={(v) => set("status", v || "To do")} options={stages} /></Field>
        <Field label="Priority"><Select value={f.priority ?? "Normal"} onChange={(v) => set("priority", (v || "Normal") as Task["priority"])} options={PRIORITIES} /></Field>
        {f.status === "Blocked" && <Field label="Blocked on" wide><input className="input" value={f.blocked_reason ?? ""} onChange={(e) => set("blocked_reason", e.target.value)} placeholder="Waiting for…" /></Field>}
        <Field label="Repeat"><Select value={f.repeat ?? ""} onChange={(v) => set("repeat", (v || null) as Task["repeat"])} placeholder="Doesn't repeat"
          options={Object.entries(REPEAT).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="About">
          {person ? <div className="row-inline" style={{ margin: 0 }}><strong>{person.name}</strong><button className="btn small ghost" onClick={() => setPerson(null)}>Change</button></div> : <>
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a person (optional)" />
            {(found.data ?? []).map((p) => <button key={p.id} className="btn ghost small" style={{ justifyContent: "flex-start" }} onClick={() => { setPerson({ id: p.id, name: p.full_name }); setQ(""); }}>{p.full_name}</button>)}
          </>}
        </Field>
        <Field label="Notes" wide><textarea className="textarea" rows={3} value={f.notes ?? ""} onChange={(e) => set("notes", e.target.value)} /></Field>
      </div>

      <div className="sol-editor">
        <div className="sol-head"><strong>Checklist</strong>{cl.length > 0 && <span className="small muted">{cl.filter((c) => c.done).length} of {cl.length} done</span>}</div>
        {cl.map((c, i) => (
          <label key={i} className="check-item">
            <input type="checkbox" checked={c.done} onChange={() => set("checklist", cl.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))} />
            <span className={c.done ? "muted" : ""} style={{ textDecoration: c.done ? "line-through" : undefined }}>{c.text}</span>
            <button className="chip-x" aria-label="Remove item" onClick={(e) => { e.preventDefault(); set("checklist", cl.filter((_, j) => j !== i)); }}>×</button>
          </label>
        ))}
        <div className="row-inline" style={{ margin: 0 }}>
          <input className="input" value={item} onChange={(e) => setItem(e.target.value)} placeholder="Add a step"
            onKeyDown={(e) => { if (e.key === "Enter" && item.trim()) { e.preventDefault(); set("checklist", [...cl, { text: item.trim(), done: false }]); setItem(""); } }} />
          <button className="btn small" onClick={() => { if (item.trim()) { set("checklist", [...cl, { text: item.trim(), done: false }]); setItem(""); } }}>Add</button>
        </div>
      </div>

      {task && (
        <div className="sol-editor">
          <strong>Comments</strong>
          {(comments.data ?? []).length === 0 && <div className="small muted">No comments yet.</div>}
          {(comments.data ?? []).map((c) => (
            <div key={c.id} className="comment">
              <div className="small muted">{(people.data ?? []).find((p) => p.user_id === c.author)?.display_name ?? "Someone"} · {dayjs(c.created_at).format("MMM D, h:mm A")}</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
            </div>
          ))}
          <div className="row-inline" style={{ margin: 0 }}>
            <input className="input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment" onKeyDown={(e) => e.key === "Enter" && addComment()} />
            <button className="btn small" onClick={addComment}>Post</button>
          </div>
        </div>
      )}
      {task && <p className="small muted">Created {dayjs(task.created_at).format("MMM D, YYYY")}{task.source === "handoff" ? " automatically (hand-off)" : task.source === "repeat" ? " by a repeat" : ""}{task.completed_at ? ` · done ${dayjs(task.completed_at).format("MMM D, h:mm A")}` : ""}.</p>}
    </Sheet>
  );
}

/** My open tasks (mine + my role's queue). Used by the Command Center. */
export const useMyTasks = () => {
  const { session, profile } = useSession();
  const me = session?.user.id;
  return useQuery(me ? `tasks:mine:${me}` : null, () => db.select<Task>("tasks", {
    select: SELECT, status: "neq.Done",
    or: `(owner_user_id.eq.${me},owner_role.eq.${profile?.role_key ?? "none"})`,
    order: "due_on.asc.nullslast,priority",
  }));
};

const PRANK: Record<string, number> = { Urgent: 0, High: 1, Normal: 2, Low: 3 };
const sortTasks = (a: Task, b: Task) =>
  (a.status === "Done" ? 1 : 0) - (b.status === "Done" ? 1 : 0) ||
  (a.due_on ?? "9999").localeCompare(b.due_on ?? "9999") || PRANK[a.priority] - PRANK[b.priority] || a.title.localeCompare(b.title);

/** Tasks page: My tasks / Everyone, as a list or a board by status. */
export function TasksPage() {
  const { query } = useRoute();
  const { session, profile, can } = useSession();
  const me = session?.user.id;
  const people = useAssignees();
  const scope = (query.get("scope") ?? "mine") as "mine" | "all";
  const view = (query.get("view") ?? "list") as "list" | "board";
  const showDone = query.get("done") === "1";
  const groupBy = (query.get("group") ?? "stage") as "stage" | "date";
  const stages = useTaskStages();
  const [editStages, setEditStages] = useState(false);
  const ownerF = query.get("owner") ?? "";
  const [open, setOpen] = useState<Task | null | "new">(null);
  const [quick, setQuick] = useState("");
  const toast = useToast();
  const q = useQuery(`tasks:list:${scope}:${showDone}`, () => db.select<Task>("tasks", {
    select: SELECT, ...(showDone ? { completed_at: `gte.${dayjs().subtract(30, "day").toISOString()}` } : { status: "neq.Done" }),
    ...(scope === "mine" ? { or: `(owner_user_id.eq.${me},owner_role.eq.${profile?.role_key ?? "none"})` } : {}),
    order: "due_on.asc.nullslast",
  }));
  const deep = query.get("task");
  const deepTask = useQuery(deep ? `tasks:one:${deep}` : null, () => db.one<Task>("tasks", { id: eq(deep!), select: SELECT }));
  const rows = useMemo(() => (q.data ?? [])
    .filter((t) => !ownerF || (ownerF.startsWith("u:") ? t.owner_user_id === ownerF.slice(2) : t.owner_role === ownerF.slice(2)))
    .sort(sortTasks), [q.data, ownerF]);
  const groups = useMemo(() => {
    const g: { key: string; title: string; tone?: string; list: Task[] }[] = [
      { key: "late", title: "Late", tone: "alert", list: [] }, { key: "today", title: "Today", list: [] },
      { key: "week", title: "Next 7 days", list: [] }, { key: "later", title: "Later", list: [] }, { key: "none", title: "No date", list: [] },
      { key: "done", title: "Done in the last 30 days", list: [] }];
    for (const t of rows) {
      const l = lateBy(t);
      const k = t.status === "Done" ? "done" : l == null ? "none" : l > 0 ? "late" : l === 0 ? "today" : l >= -7 ? "week" : "later";
      g.find((x) => x.key === k)!.list.push(t);
    }
    if (groupBy === "stage") {
      return stages.filter((st) => st !== "Done" || showDone).map((st) => ({ key: `s:${st}`, title: st === "Done" ? "Done in the last 30 days" : st,
        tone: st === "Blocked" ? "alert" : undefined, list: rows.filter((t) => t.status === st) }))
        .concat([{ key: "s:other", title: "Other", tone: undefined, list: rows.filter((t) => !stages.includes(t.status)) }])
        .filter((x) => x.list.length);
    }
    return g.filter((x) => x.list.length);
  }, [rows, groupBy, stages, showDone]);

  async function quickAdd() {
    if (!quick.trim()) return;
    // "!" anywhere = urgent; "today" / "tomorrow" set the date
    const text = quick.trim();
    const due = /\btoday\b/i.test(text) ? dayjs() : /\btomorrow\b/i.test(text) ? dayjs().add(1, "day") : null;
    await db.insert("tasks", { title: text.replace(/\s*!\s*/g, " ").replace(/\b(today|tomorrow)\b/i, "").trim() || text,
      priority: text.includes("!") ? "Urgent" : "Normal", due_on: due?.format("YYYY-MM-DD") ?? null, owner_user_id: me });
    setQuick(""); invalidate("tasks"); invalidate("cc"); toast("Task added");
  }
  async function move(t: Task, status: string) {
    if (status === "Blocked") return setOpen({ ...t, status: "Blocked" });
    await db.update("tasks", { id: eq(t.id) }, { status }); invalidate("tasks"); invalidate("cc");
  }

  const ownerOptions = [
    ...(people.data ?? []).map((p) => ({ value: `u:${p.user_id}`, label: p.user_id === me ? "Me" : p.display_name })),
    ...Object.entries(ROLE_QUEUE).map(([k, l]) => ({ value: `r:${k}`, label: `Queue · ${l.split(" (")[0]}` })),
  ];
  const canAll = can("users", "view") || (people.data ?? []).length > 0;

  return (
    <>
      <PageHead title="Tasks" sub="Everything someone has to do, with one owner and a date. Assign to a person or to the Assistant queue."
        actions={can("tasks", "edit") && <button className="btn primary" onClick={() => setOpen("new")}>New task</button>} />
      {can("tasks", "edit") && (
        <div className="quick-add">
          <input className="input" value={quick} onChange={(e) => setQuick(e.target.value)} onKeyDown={(e) => e.key === "Enter" && quickAdd()}
            placeholder="Quick add for yourself — e.g. “Send Ada the RESP illustration tomorrow” (add ! for urgent), press Enter" />
        </div>
      )}
      <div className="filters">
        <Seg value={scope} onChange={(v) => setQuery({ scope: v === "mine" ? null : v })} options={[{ value: "mine", label: "My tasks" }, ...(canAll ? [{ value: "all" as const, label: "Everyone's" }] : [])]} />
        <Seg value={view} onChange={(v) => setQuery({ view: v === "list" ? null : v })} options={[{ value: "list", label: "List" }, { value: "board", label: "Board" }]} />
        {view === "list" && <Seg value={groupBy} onChange={(v) => setQuery({ group: v === "stage" ? null : v })} options={[{ value: "stage", label: "By stage" }, { value: "date", label: "By date" }]} />}
        {(can("settings", "edit") || profile?.role_key === "owner" || profile?.role_key === "admin") && <button className="btn small ghost" onClick={() => setEditStages(true)}>Edit stages</button>}
        {scope === "all" && <Select value={ownerF} onChange={(v) => setQuery({ owner: v || null })} options={ownerOptions} placeholder="Any owner" style={{ width: 200 }} />}
        <label className="switch-row" style={{ marginTop: 0 }}><input type="checkbox" checked={showDone} onChange={(e) => setQuery({ done: e.target.checked ? "1" : null })} /> Show done</label>
      </div>
      <ErrorBox error={q.error} />
      {q.loading && !q.data ? <Panel><Loading rows={6} /></Panel> : rows.length === 0 ? (
        <Panel><Empty title={scope === "mine" ? "Nothing on your plate" : "No open tasks"}>Add one above, or from any person with “Add task”.</Empty></Panel>
      ) : view === "list" ? (
        <div style={{ display: "grid", gap: 14 }}>
          {groups.map((g) => (
            <Panel key={g.key} title={`${g.title} (${g.list.length})`} className={g.tone === "alert" ? "panel-alert" : undefined}>
              <div className="task-list2">{g.list.map((t) => <TaskRow key={t.id} t={t} onOpen={setOpen} people={people.data} me={me} compact={scope === "mine"} />)}</div>
            </Panel>
          ))}
        </div>
      ) : (
        <div className="tboard">
          {stages.filter((s) => s !== "Done" || showDone).map((s) => (
            <BoardCol key={s} title={s} count={rows.filter((t) => t.status === s).length}
              onDrop={(id) => { const t = rows.find((x) => x.id === id); if (t && t.status !== s) move(t, s); }}>
              {rows.filter((t) => t.status === s).map((t) => (
                <div key={t.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)}>
                  <TaskRow t={t} onOpen={setOpen} people={people.data} me={me} compact={scope === "mine"} />
                </div>
              ))}
            </BoardCol>
          ))}
        </div>
      )}
      {open && <TaskSheet task={open === "new" ? null : open} onClose={() => setOpen(null)} />}
      {editStages && <StagesSheet stages={stages} onClose={() => setEditStages(false)} />}
      {deep && deepTask.data && !open && <TaskSheet task={deepTask.data} onClose={() => setQuery({ task: null })} />}
    </>
  );
}

function BoardCol({ title, count, children, onDrop }: { title: string; count: number; children: ReactNode; onDrop: (id: string) => void }) {
  const [over, setOver] = useState(false);
  return (
    <div className={clsx("board-col", over && "over")} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain")); }}>
      <div className="board-h"><strong>{title} <span className="muted">({count})</span></strong></div>
      <div className="board-body">{children}</div>
    </div>
  );
}

/** Person drawer: their open tasks + add one. */
export function PersonTasks({ person }: { person: { id: string; full_name: string } }) {
  const { can, session } = useSession();
  const q = useQuery(`tasks:person:${person.id}`, () => db.select<Task>("tasks", { select: SELECT, person_id: eq(person.id), order: "due_on.asc.nullslast" }));
  const [open, setOpen] = useState<Task | null | "new">(null);
  const [tab, setTab] = useState<"open" | "done">("open");
  if (!can("tasks")) return null;
  const all = q.data ?? [];
  const openList = all.filter((t) => t.status !== "Done").sort(sortTasks);
  const doneList = all.filter((t) => t.status === "Done").sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));
  const list = tab === "open" ? openList : doneList;
  return (
    <div className="appt-person">
      <div className="sol-head"><strong>Tasks</strong>
        <div className="seg small" role="group" aria-label="Open or done tasks">
          <button aria-pressed={tab === "open"} onClick={() => setTab("open")}>Open ({openList.length})</button>
          <button aria-pressed={tab === "done"} onClick={() => setTab("done")}>Done ({doneList.length})</button>
        </div>
        {can("tasks", "edit") && <button className="btn small" onClick={() => setOpen("new")}>Add task</button>}</div>
      {list.length === 0 ? <div className="small muted">{tab === "open" ? "No open tasks." : "Nothing done yet."}</div>
        : <div className="task-list2">{list.map((t) => (
          <div key={t.id}>
            <TaskRow t={t} onOpen={setOpen} me={session?.user.id} />
            {tab === "done" && t.completed_at && <div className="small muted" style={{ margin: "-2px 0 6px 34px" }}>Done {dayjs(t.completed_at).format("MMM D, YYYY")} · tap the tick to reopen</div>}
          </div>))}</div>}
      {open && <TaskSheet task={open === "new" ? null : open} preset={{ person_id: person.id, person_name: person.full_name }} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Tasks → Edit stages: add, rename, reorder, remove. To do and Done stay (first and last). */
function StagesSheet({ stages, onClose }: { stages: string[]; onClose: () => void }) {
  const toast = useToast();
  type Row = { name: string; was: string | null; moveTo?: string };
  const [rows, setRows] = useState<Row[]>(stages.filter((s) => s !== "To do" && s !== "Done").map((s) => ({ name: s, was: s })));
  const [removed, setRemoved] = useState<Row[]>([]);
  const [err, setErr] = useState("");
  const set = (i: number, p: Partial<Row>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const swap = (i: number, j: number) => setRows((r) => { if (j < 0 || j >= r.length) return r; const n = [...r]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const keep = rows.map((r) => r.name.trim()).filter(Boolean);
  const targets = ["To do", ...keep, "Done"];
  async function save() {
    setErr("");
    const names = rows.map((r) => r.name.trim());
    if (names.some((n) => !n)) return setErr("Give every stage a name, or remove it.");
    if (new Set(names.map((n) => n.toLowerCase())).size !== names.length || names.some((n) => /^(to do|done)$/i.test(n))) return setErr("Each stage needs its own name (To do and Done are already there).");
    const renames = Object.fromEntries(rows.filter((r) => r.was && r.was !== r.name.trim()).map((r) => [r.was!, r.name.trim()]));
    const moves = Object.fromEntries(removed.filter((r) => r.was).map((r) => [r.was!, r.moveTo || "To do"]));
    try {
      await db.rpc("task_stages_save", { p_stages: names, p_renames: renames, p_moves: moves });
      invalidate("ref:"); invalidate("tasks"); invalidate("cc"); toast("Task stages saved"); onClose();
    } catch (e: any) { setErr(e.message ?? String(e)); }
  }
  return (
    <Sheet title="Task stages" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={save}>Save</button></>}>
      <p className="small muted" style={{ marginTop: 0 }}>The columns on the board and the groups in the list, in this order. New tasks start in To do; Done is always last.</p>
      <div className="stage-edit">
        <div className="stage-row fixed"><span>To do</span><span className="small muted">first</span></div>
        {rows.map((r, i) => (
          <div key={i} className="stage-row">
            <input className="input" value={r.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Stage name" />
            <button className="icon-btn" aria-label="Move up" disabled={i === 0} onClick={() => swap(i, i - 1)}>↑</button>
            <button className="icon-btn" aria-label="Move down" disabled={i === rows.length - 1} onClick={() => swap(i, i + 1)}>↓</button>
            <button className="btn small ghost" onClick={() => { setRemoved((x) => [...x, { ...r, moveTo: "To do" }]); setRows((x) => x.filter((_, j) => j !== i)); }}>Remove</button>
          </div>))}
        <div className="stage-row fixed"><span>Done</span><span className="small muted">last</span></div>
      </div>
      <button className="btn small" style={{ marginTop: 8 }} onClick={() => setRows((x) => [...x, { name: "", was: null }])}>+ Add a stage</button>
      {removed.filter((r) => r.was).length > 0 && <div style={{ marginTop: 14 }}>
        <h4 style={{ margin: "0 0 6px" }}>Removed: where do their tasks go?</h4>
        {removed.filter((r) => r.was).map((r) => (
          <div key={r.was} className="stage-row"><span style={{ flex: 1 }}>{r.was}</span>
            <Select value={r.moveTo ?? "To do"} onChange={(v) => setRemoved((x) => x.map((y) => (y.was === r.was ? { ...y, moveTo: v || "To do" } : y)))} options={targets} style={{ width: 180 }} />
            <button className="btn small ghost" onClick={() => { setRows((x) => [...x, { name: r.was!, was: r.was }]); setRemoved((x) => x.filter((y) => y.was !== r.was)); }}>Keep</button>
          </div>))}
      </div>}
      {err && <p className="error-text">{err}</p>}
    </Sheet>
  );
}
